import React, { useState, useMemo, useContext, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardTitle, Button, Input, FormGroup, Label, Modal, ModalHeader, ModalBody, Alert, Spinner, Pagination, PaginationItem, PaginationLink, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, InputGroup, Breadcrumb } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useHistory } from "react-router-dom";
import { getSystemConnections } from "./SystemConnectionController";
import { Context } from '../../../app-contexts/CommonContexts';
import { MetaTags } from "react-meta-tags";

const SystemConnections = () => {
    const history = useHistory();
    const correctPassword = "system@admin123"; // You can change this to any password you want

    const {
        isHeadOffice, getMenuDetails, masterAuditLog, getSelectedMasterPermissions, fetchAutoCode
    } = useContext(Context);

    // State for modal and password validation
    const [showPasswordModal, setShowPasswordModal] = useState(true);
    const [password, setPassword] = useState("");
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [invalidAttempt, setInvalidAttempt] = useState(false);
    const [attemptsLeft, setAttemptsLeft] = useState(3);

    // State for API data
    const [connectionTable1, setConnectionTable1] = useState([]);
    const [connectionTable2, setConnectionTable2] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Search and pagination state for connectionTable2
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [5, 10, 25, 50, 100];

    const [menuName, SetMenuName] = useState("")
    const [parentName, SetParentName] = useState("")

    useEffect(async () => {

        var obj = JSON.parse(localStorage.getItem("authUser"))
        var LogUserType = obj[2]
        var menuDetail = await getMenuDetails(window.location.pathname)
        var menu = menuDetail && menuDetail.menuTag ? menuDetail.menuTag : history.push('/dashboard');
        var parentname = menuDetail?.parentName
        SetParentName(parentname)

        var responseData = await getSelectedMasterPermissions(LogUserType, menu)

        if (!responseData.strMenuAccessRight) {
            history.push('/dashboard')
        }

        SetMenuName(responseData.strMenuName)

    }, [])


    // Function to fetch connection data from API
    const fetchConnectionData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Using the controller to fetch all connections
            const { bkList, posList } = await getSystemConnections();

            setConnectionTable1(bkList);
            setConnectionTable2(posList);
        } catch (err) {
            console.error("Error fetching connection data:", err);
            setError("Failed to load connection data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    // Search and pagination logic for connectionTable2
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return connectionTable2;

        return connectionTable2.filter(row => {
            const searchTerm = searchQuery.toLowerCase();
            return (
                (row.location && row.location.toLowerCase().includes(searchTerm)) ||
                (row.ip_address && row.ip_address.toLowerCase().includes(searchTerm)) ||
                (row.db_name && row.db_name.toLowerCase().includes(searchTerm))
            );
        });
    }, [connectionTable2, searchQuery]);

    // Pagination logic
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredData.slice(startIndex, startIndex + pageSize);
    }, [filteredData, currentPage, pageSize]);

    // Total pages calculation
    const totalPages = useMemo(() => {
        return Math.ceil(filteredData.length / pageSize);
    }, [filteredData, pageSize]);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) return;
        setCurrentPage(pageNumber);
    };

    // Handle page size change
    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    // Handle the password submission
    const handlePasswordSubmit = () => {
        if (password === correctPassword) {
            setIsAuthorized(true);
            setShowPasswordModal(false);
            setPassword("");

            // Fetch connection data only after successful password authentication
            fetchConnectionData();
        } else {
            setInvalidAttempt(true);
            setAttemptsLeft(attemptsLeft - 1);

            if (attemptsLeft <= 1) {
                // Redirect to dashboard after 3 failed attempts
                setTimeout(() => {
                    history.push("/dashboard");
                }, 2000);
            }

            setPassword("");
        }
    };

    // Keypress handler for Enter key
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handlePasswordSubmit();
        }
    };

    // Loading state component
    const LoadingState = () => (
        <div className="text-center p-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading connection data...</p>
        </div>
    );

    // Error state component
    const ErrorState = () => (
        <div className="text-center p-5">
            <div className="text-danger mb-3">
                <i className="mdi mdi-alert-circle-outline display-4"></i>
            </div>
            <h5 className="text-danger">Error Loading Data</h5>
            <p>{error}</p>
            <Button color="primary" onClick={fetchConnectionData}>
                Retry
            </Button>
        </div>
    );

    return (
        <React.Fragment>
            <MetaTags>
                <title>{menuName}</title>
            </MetaTags>
            {/* Password protection modal */}
            <Modal
                isOpen={showPasswordModal}
                centered
                backdrop="static"
                keyboard={false}
            >
                <ModalHeader className="bg-primary text-center" style={{ color: "white" }}>
                    <h5 className="text-white m-0 w-100 text-center">Restricted Area - Authorization Required</h5>
                </ModalHeader>
                <ModalBody>
                    <p className="mb-4">
                        This page contains sensitive system connection information.
                        Please enter the system administrator password to continue.
                    </p>

                    {invalidAttempt && (
                        <Alert color="danger">
                            Invalid password! Attempts left: {attemptsLeft}
                            {attemptsLeft <= 1 && (
                                <div className="mt-2">Redirecting to dashboard...</div>
                            )}
                        </Alert>
                    )}

                    <FormGroup>
                        <Label for="systemPassword">System Password</Label>
                        <Input
                            type="password"
                            id="systemPassword"
                            placeholder="Enter system password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={handleKeyPress}
                            autoFocus
                        />
                    </FormGroup>

                    <div className="text-end mt-4">
                        <Button
                            color="secondary"
                            className="me-2"
                            onClick={() => history.push("/dashboard")}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={handlePasswordSubmit}
                            disabled={!password}
                        >
                            Submit
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            {/* Only show page content if authorized */}
            {isAuthorized && (
                <div className="page-content">
                    <Container fluid>
                        <Breadcrumbs title={parentName} breadcrumbItem={menuName} />
                        {/* Display loading or error states */}
                        {loading ? (
                            <LoadingState />
                        ) : error ? (
                            <ErrorState />
                        ) : (
                            <>
                                {/* First Table */}
                                <Row>
                                    <Col lg={12}>
                                        <Card>
                                            <CardBody>
                                                <CardTitle className="mb-4">BackOffice Connection</CardTitle>
                                                <div style={{
                                                    overflowX: 'auto',
                                                    width: '100%',
                                                    display: 'block',
                                                    whiteSpace: 'nowrap',
                                                    position: 'relative'
                                                }}>
                                                    <table className="table table-bordered table-sm" style={{ minWidth: '800px' }}>
                                                        <thead className='table-secondary'>
                                                            <tr>
                                                                {/* <th style={{ minWidth: '50px' }}>#</th> */}
                                                                {/* <th style={{ minWidth: '100px' }}>Location</th> */}
                                                                <th style={{ minWidth: '400px' }}>IP Address</th>
                                                                <th style={{ minWidth: '150px' }}>DB</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {connectionTable1.length > 0 ? (
                                                                connectionTable1.map((row, index) => (
                                                                    <tr key={row.id || index}>
                                                                        {/* <td>{index + 1}</td> */}
                                                                        {/* <td>{row.location}</td> */}
                                                                        <td>{row.serverCentral}</td>
                                                                        <td>{row.dbNameCentral}</td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="4" className="text-center">No primary connections found</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Second Table */}
                                <Row>
                                    <Col lg={12}>
                                        <Card>
                                            <CardBody>
                                                <CardTitle className="mb-4">POS Connections</CardTitle>
                                                <Row>
                                                    <InputGroup className="mb-3">
                                                        <Input
                                                            type="text"
                                                            placeholder="Search connections..."
                                                            value={searchQuery}
                                                            onChange={handleSearchChange}
                                                            size={"sm"}
                                                        />
                                                        <UncontrolledDropdown size="sm">
                                                            <DropdownToggle caret color="secondary">
                                                                Page Size: {pageSize}
                                                            </DropdownToggle>
                                                            <DropdownMenu>
                                                                {pageSizeOptions.map(size => (
                                                                    <DropdownItem key={size} onClick={() => handlePageSizeChange(size)}>
                                                                        {size}
                                                                    </DropdownItem>
                                                                ))}
                                                            </DropdownMenu>
                                                        </UncontrolledDropdown>
                                                    </InputGroup>
                                                </Row>
                                                <div style={{
                                                    overflowX: 'auto',
                                                    width: '100%',
                                                    display: 'block',
                                                    whiteSpace: 'nowrap',
                                                    position: 'relative'
                                                }}>
                                                    <table className="table table-bordered table-sm" style={{ minWidth: '800px' }}>
                                                        <thead className='table-secondary'>
                                                            <tr>
                                                                <th style={{ minWidth: '50px' }}>#</th>
                                                                <th style={{ minWidth: '100px' }}>Location</th>
                                                                <th style={{ minWidth: '100px' }}>Terminal ID</th>
                                                                <th style={{ minWidth: '400px' }}>IP Address</th>
                                                                <th style={{ minWidth: '150px' }}>DB Name</th>
                                                                <th style={{ minWidth: '150px' }}>API URL Outlet</th>
                                                                <th style={{ minWidth: '150px' }}>Server Outlet</th>
                                                                <th style={{ minWidth: '150px' }}>DB Name Outlet</th>
                                                                <th style={{ minWidth: '150px' }}>API URL Central</th>
                                                                <th style={{ minWidth: '150px' }}>Server Central</th>
                                                                <th style={{ minWidth: '150px' }}>DB Name Central</th>
                                                                <th style={{ minWidth: '150px' }}>API URL Local</th>
                                                                <th style={{ minWidth: '150px' }}>Server Local</th>
                                                                <th style={{ minWidth: '150px' }}>DB Name Local</th>
                                                                <th style={{ minWidth: '150px' }}>Log Date Time</th>
                                                                <th style={{ minWidth: '150px' }}>URL Central Loyalty</th>
                                                                <th style={{ minWidth: '150px' }}>Server Central Loyalty</th>
                                                                <th style={{ minWidth: '150px' }}>DB Name Central Loyalty</th>
                                                                <th style={{ minWidth: '150px' }}>API URL Local Loyalty</th>
                                                                <th style={{ minWidth: '150px' }}>Server Local Loyalty</th>
                                                                <th style={{ minWidth: '150px' }}>DB Name Local Loyalty</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {paginatedData.length > 0 ? (
                                                                paginatedData.map((row, index) => (
                                                                    <tr key={row.id || index}>
                                                                        <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                                                        <td>{row.location}</td>
                                                                        <td>{row.terminalId}</td>
                                                                        <td>{row.ipAddress}</td>
                                                                        <td>{row.dbNameOutlet}</td>
                                                                        <td>{row.apiUrlOutlet}</td>
                                                                        <td>{row.serverOutlet}</td>
                                                                        <td>{row.dbNameOutlet}</td>
                                                                        <td>{row.apiUrlCentral}</td>
                                                                        <td>{row.serverCentral}</td>
                                                                        <td>{row.dbNameCentral}</td>
                                                                        <td>{row.apiUrlLocal}</td>
                                                                        <td>{row.serverLocal}</td>
                                                                        <td>{row.dbNameLocal}</td>
                                                                        <td>{row.logDateTime ? row.logDateTime.toLocaleString() : ''}</td>
                                                                        <td>{row.urlCentralLoyalty}</td>
                                                                        <td>{row.serverCentralLoyalty}</td>
                                                                        <td>{row.dbNameCentralLoyalty}</td>
                                                                        <td>{row.apiUrlLocalLoyalty}</td>
                                                                        <td>{row.serverLocalLoyalty}</td>
                                                                        <td>{row.dbNameLocalLoyalty}</td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="21" className="text-center">No secondary connections found</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>

                                                    <Pagination size="sm" className="mt-3">
                                                        <PaginationItem disabled={currentPage === 1}>
                                                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                                                        </PaginationItem>
                                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                                                            <PaginationItem active={pageNumber === currentPage} key={pageNumber}>
                                                                <PaginationLink onClick={() => handlePageChange(pageNumber)}>
                                                                    {pageNumber}
                                                                </PaginationLink>
                                                            </PaginationItem>
                                                        ))}
                                                        <PaginationItem disabled={currentPage === totalPages}>
                                                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                                                        </PaginationItem>
                                                    </Pagination>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Container>
                </div>
            )}
        </React.Fragment>
    );
};

export default SystemConnections;